/**
 * <kb-gallery-nav> - Kate Barton product gallery arrows
 *
 * Replaces the theme's carousel-prev-button / carousel-next-button for the
 * product gallery. Those were the source of the missing left arrow: on the
 * carousel's start edge - the state on page load - CarouselPrevButton sets the
 * `disabled` attribute on its own inner <button>, so the left arrow was born
 * disabled on every product page and its appearance depended on whatever
 * happened to style a disabled button.
 *
 * This element owns the whole decision instead:
 *
 *   - it reads the track's scrollLeft to work out where it is, rather than
 *     trusting the theme's scroll:edge-nearing / scroll:edge-leaving events
 *   - it drives the track through scroll-carousel's public previous() / next()
 *     / select() API when theme.js is up, and falls back to a plain
 *     scrollBy() when it is not, so the arrows work either way
 *   - it states edges with `aria-disabled`, never `disabled` - no theme rule
 *     or theme script reaches for that attribute, so nothing outside this file
 *     and its CSS can make an arrow disappear
 *
 * data-edge controls what reaching an edge means:
 *   loop  wrap around; neither arrow is ever inert (default)
 *   dim   stay visible, plainly inactive
 *   hide  disappear at that edge, the way the theme's own carousels behave
 *
 * Loaded as a classic script, ahead of theme.js's deferred module, for the
 * same reason kb-sticky-bar.js is: it must not depend on module ordering. It
 * therefore cannot assume the carousel has been upgraded yet, and does not.
 */
(function () {
  'use strict';

  var NAME = 'kb-gallery-nav';

  if (!('customElements' in window) || window.customElements.get(NAME)) return;

  /* A couple of pixels of slack: scrollLeft is fractional under zoom and on
     high-DPI displays, so an exact comparison never reports an edge. */
  var SLACK = 2;

  function atStart(track) {
    return Math.abs(track.scrollLeft) <= SLACK;
  }

  function atEnd(track) {
    return Math.abs(track.scrollLeft) >= track.scrollWidth - track.clientWidth - SLACK;
  }

  function scrollable(track) {
    return track.scrollWidth - track.clientWidth > SLACK;
  }

  var KbGalleryNav = class extends HTMLElement {
    connectedCallback() {
      this.edge = this.dataset.edge || 'loop';
      this.prevButton = this.querySelector('[data-kb-gnav="prev"]');
      this.nextButton = this.querySelector('[data-kb-gnav="next"]');

      /* Bound once, not per connect: product-rerender moves this element on a
         variant change, so connectedCallback can run more than once and a
         fresh bound function each time would leak a listener. */
      if (!this.handlersBound) {
        this.onClick = this.onClick.bind(this);
        this.sync = this.sync.bind(this);
        this.attach = this.attach.bind(this);
        this.handlersBound = true;
      }

      this.addEventListener('click', this.onClick);
      this.attach();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.detach();
    }

    /* Resolved on every use rather than cached at connect: product-rerender
       swaps the gallery out on a variant change, so the element we were
       holding can be replaced under us. */
    get track() {
      var id = this.dataset.carousel;
      return id ? document.getElementById(id) : null;
    }

    attach() {
      var track = this.track;

      if (!track) {
        /* The track is further down the same section, so during a classic
           script's mid-parse upgrade it does not exist yet. Wait for the
           document rather than throwing, which is what the theme's own button
           does in this situation. */
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.attach, { once: true });
        }
        return;
      }

      if (this.bound === track) return;
      this.detach();
      this.bound = track;

      track.addEventListener('scroll', this.sync, { passive: true });
      track.addEventListener('carousel:select', this.sync);
      window.addEventListener('resize', this.sync);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.sync);
        this.resizeObserver.observe(track);
      }

      this.sync();
    }

    detach() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.bound) {
        this.bound.removeEventListener('scroll', this.sync);
        this.bound.removeEventListener('carousel:select', this.sync);
        this.bound = null;
      }
      window.removeEventListener('resize', this.sync);
    }

    /* One state write per frame. `scroll` fires far more often than that. */
    sync() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(function () {
        this.frame = null;

        var track = this.track;
        if (!track) return this.attach();

        /* Only one image, or a grid layout where everything is already on
           screen: there is nothing for an arrow to do. */
        this.toggleAttribute('data-single', !scrollable(track));

        var start = atStart(track);
        var end = atEnd(track);

        this.toggleAttribute('data-at-start', start);
        this.toggleAttribute('data-at-end', end);

        /* Under `loop` an edge is not a dead end, so neither button is ever
           marked inactive. */
        var inert = this.edge !== 'loop';

        if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(inert && start));
        if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(inert && end));
      }.bind(this));
    }

    onClick(event) {
      var button = event.target.closest('[data-kb-gnav]');
      if (!button || !this.contains(button)) return;
      if (button.getAttribute('aria-disabled') === 'true') return;

      var track = this.track;
      if (!track) return;

      this.go(track, button.dataset.kbGnav === 'next');
    }

    go(track, forward) {
      var cells = track.cells;
      var hasApi = typeof track.next === 'function' &&
        typeof track.previous === 'function' &&
        typeof track.select === 'function' &&
        cells && typeof cells.length === 'number';

      if (hasApi) {
        if (this.edge === 'loop' && cells.length > 1) {
          if (forward && atEnd(track)) return track.select(0);
          if (!forward && atStart(track)) return track.select(cells.length - 1);
        }

        return forward ? track.next() : track.previous();
      }

      /* theme.js has not upgraded the track yet, or never will. It is still a
         native scroll-snap scroller, so move it by hand. */
      var step = track.clientWidth || 1;
      var direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;

      track.scrollBy({ left: step * direction * (forward ? 1 : -1), behavior: 'smooth' });
    }
  };

  window.customElements.define(NAME, KbGalleryNav);
})();
